// ===== 신강/신약(身强身弱) + 용신(用神) 계산 엔진 =====

import { STEM_ELEMENTS, BRANCH_ELEMENTS, type Element, type SajuResult } from './sajuCalculations';
import { JIJANG, ELEMENT_IDX, GENERATES, CONTROLS, GENERATED_BY, CONTROLLED_BY } from './sipseongCalculations';

// ===== 오행별 지지 계절 왕상휴수사 (월지 기준) =====
// 월지 인덱스: 자=0, 축=1, 인=2, 묘=3, 진=4, 사=5, 오=6, 미=7, 신=8, 유=9, 술=10, 해=11
// 계절별 오행 힘: 왕(旺)=4, 상(相)=3, 휴(休)=2, 수(囚)=1, 사(死)=1
// 목: 봄(인묘) 왕, 여름(사오) 휴, 가을(신유) 사, 겨울(해자) 상, 환절기(진술축미) 수
const SEASON_POWER: Record<Element, number[]> = {
  목: [3, 1, 4, 4, 2, 1, 2, 1, 1, 1, 2, 3],  // 자축인묘진사오미신유술해
  화: [1, 1, 3, 3, 3, 4, 4, 2, 1, 1, 1, 1],
  토: [2, 4, 2, 1, 4, 2, 2, 4, 2, 1, 4, 1],
  금: [1, 2, 1, 1, 3, 3, 1, 3, 4, 4, 3, 2],
  수: [4, 3, 1, 1, 2, 1, 1, 2, 3, 2, 2, 4],
};

// ===== 신강/신약 점수 계산 =====
export interface SingangyakScore {
  total: number;
  rawScore: number;
  maxPossible: number;
  deotnyeong: boolean;
  deotji: boolean;
  deotseScore: number;
  level: SingangyakLevel;
  label: string;
  labelEn: string;
  description: string;
  descriptionEn: string;
  emoji: string;
}

export type SingangyakLevel = 'extreme_weak' | 'weak' | 'mild_weak' | 'balanced' | 'mild_strong' | 'strong' | 'extreme_strong';

const LEVEL_INFO: Record<SingangyakLevel, { label: string; labelEn: string; description: string; descriptionEn: string; emoji: string }> = {
  extreme_weak:  {
    label: '극신약(極身弱)', labelEn: 'Very Weak (極身弱)',
    description: '환경의 영향을 강하게 받는 유형이에요. 섬세하고 감수성이 풍부하지만 쉽게 지칠 수 있어요.',
    descriptionEn: 'Highly susceptible to environmental influence. Sensitive and emotionally rich, but prone to burnout.',
    emoji: '🌿',
  },
  weak: {
    label: '신약(身弱)', labelEn: 'Weak (身弱)',
    description: '주변 환경을 스펀지처럼 흡수하는 섬세하고 유연한 타입이에요.',
    descriptionEn: 'You absorb the surrounding environment like a sponge — delicate, flexible, and highly adaptable.',
    emoji: '🍃',
  },
  mild_weak: {
    label: '중화(中和) - 약', labelEn: 'Slightly Weak',
    description: '약간 유연한 편으로, 협력과 소통을 통해 빛을 발해요.',
    descriptionEn: 'Slightly on the flexible side — you shine through collaboration and open communication.',
    emoji: '⚖️',
  },
  balanced: {
    label: '중화(中和)', labelEn: 'Balanced (中和)',
    description: '균형 잡힌 사주예요! 다양한 상황에 잘 적응할 수 있어요.',
    descriptionEn: 'A well-balanced chart! You adapt smoothly to a wide range of situations and challenges.',
    emoji: '✨',
  },
  mild_strong: {
    label: '중화(中和) - 강', labelEn: 'Slightly Strong',
    description: '약간 주도적인 편으로, 자신의 의지대로 상황을 이끌어가요.',
    descriptionEn: 'Slightly on the assertive side — you naturally steer situations according to your own will.',
    emoji: '🌟',
  },
  strong: {
    label: '신강(身强)', labelEn: 'Strong (身强)',
    description: '강한 주체성과 추진력을 가진 타입이에요. 독립적이고 리더십이 강해요.',
    descriptionEn: 'A type with strong self-identity and drive. Independent and naturally inclined toward leadership.',
    emoji: '💪',
  },
  extreme_strong: {
    label: '극신강(極身强)', labelEn: 'Very Strong (極身强)',
    description: '엄청난 추진력과 강한 의지력의 소유자예요. 타협보다는 주도를 선호해요.',
    descriptionEn: 'Possessed of immense drive and iron will. You prefer leading over compromising.',
    emoji: '🔥',
  },
};

// 일간을 돕는가(비겁+인성) 판단
function helpsDay(dayEl: number, targetEl: number): boolean {
  return targetEl === dayEl || targetEl === GENERATED_BY[dayEl];
}

export function calcSingangyak(result: SajuResult): SingangyakScore {
  const dayEl = ELEMENT_IDX[STEM_ELEMENTS[result.dayPillar.stemIdx]];
  const monthBranchIdx = result.monthPillar.branchIdx;

  // ── 득령(得令): 월지가 일간을 돕는가 ──
  const monthBranchEl = ELEMENT_IDX[BRANCH_ELEMENTS[monthBranchIdx]];
  const deotnyeong = helpsDay(dayEl, monthBranchEl);
  const deotnyeongScore = deotnyeong ? 30 : 0;

  // 월지 계절 보너스 - 일간 오행의 계절 힘
  const seasonalPower = SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === dayEl) as Element][monthBranchIdx];

  // ── 득지(得地): 일지가 일간을 돕는가 ──
  const dayBranchEl = ELEMENT_IDX[BRANCH_ELEMENTS[result.dayPillar.branchIdx]];
  const deotji = helpsDay(dayEl, dayBranchEl);
  const deotjiScore = deotji ? 10 : 0;

  // ── 득세(得勢): 나머지 글자들이 일간을 돕는가 ──
  const otherStems = [
    result.yearPillar.stemIdx,
    result.monthPillar.stemIdx,
    ...(result.hourPillar ? [result.hourPillar.stemIdx] : []),
  ];
  const otherBranches = [
    result.yearPillar.branchIdx,
    ...(result.hourPillar ? [result.hourPillar.branchIdx] : []),
  ];

  let deotseScore = 0;
  for (const si of otherStems) {
    if (helpsDay(dayEl, ELEMENT_IDX[STEM_ELEMENTS[si]])) deotseScore += 5;
  }
  for (const bi of otherBranches) {
    if (helpsDay(dayEl, ELEMENT_IDX[BRANCH_ELEMENTS[bi]])) deotseScore += 4;
    // 지장간 정기도 고려
    const jijangMain = JIJANG[bi][JIJANG[bi].length - 1];
    if (helpsDay(dayEl, ELEMENT_IDX[STEM_ELEMENTS[jijangMain.stemIdx]])) deotseScore += 2;
  }

  // 계절력 보정 (왕상휴수사 반영)
  const seasonBonus = (seasonalPower - 2) * 5; // 왕(4)→+10, 상(3)→+5, 휴(2)→0, 수(1)→-5

  const rawScore = deotnyeongScore + deotjiScore + deotseScore + seasonBonus;
  const maxPossible = 80; // 대략적 최대치

  // 0~100 정규화
  const total = Math.max(5, Math.min(95, Math.round((rawScore / maxPossible) * 100)));

  let level: SingangyakLevel;
  if (total <= 15) level = 'extreme_weak';
  else if (total <= 30) level = 'weak';
  else if (total <= 45) level = 'mild_weak';
  else if (total <= 55) level = 'balanced';
  else if (total <= 65) level = 'mild_strong';
  else if (total <= 80) level = 'strong';
  else level = 'extreme_strong';

  const info = LEVEL_INFO[level];
  return {
    total, rawScore, maxPossible,
    deotnyeong, deotji, deotseScore,
    level, ...info,
  };
}

// ===== 용신(用神) 추출 =====
export interface YongshinResult {
  element: Element;
  reason: string;
  description: string;
  tossMessage: string;     // 토스 스타일 메시지
  items: YongshinItem[];   // 행운 아이템
}

export interface YongshinItem {
  category: string;
  examples: string[];
}

const ELEMENT_NAMES: Record<Element, string> = {
  목: '나무(木)', 화: '불(火)', 토: '흙(土)', 금: '쇠(金)', 수: '물(水)',
};

const ELEMENT_KEYWORDS: Record<Element, string> = {
  목: '성장과 도전', 화: '열정과 표현', 토: '안정과 신뢰', 금: '결단과 정밀', 수: '지혜와 유연성',
};

const ELEMENT_ITEMS: Record<Element, YongshinItem[]> = {
  목: [
    { category: '행운 색상', examples: ['초록', '청록', '올리브'] },
    { category: '추천 방향', examples: ['동쪽', '동남쪽'] },
    { category: '행운 아이템', examples: ['화분', '나무 소품', '숲 산책'] },
    { category: '추천 직업', examples: ['교육', '의료', '환경', '농업'] },
  ],
  화: [
    { category: '행운 색상', examples: ['빨강', '주황', '핑크'] },
    { category: '추천 방향', examples: ['남쪽'] },
    { category: '행운 아이템', examples: ['촛불', '조명', '따뜻한 음식'] },
    { category: '추천 직업', examples: ['연예', '마케팅', '영업', '요식업'] },
  ],
  토: [
    { category: '행운 색상', examples: ['황토색', '갈색', '베이지'] },
    { category: '추천 방향', examples: ['중앙', '남서쪽'] },
    { category: '행운 아이템', examples: ['도자기', '황금', '농작물'] },
    { category: '추천 직업', examples: ['부동산', '건설', '금융', '유통'] },
  ],
  금: [
    { category: '행운 색상', examples: ['흰색', '금색', '은색', '회색'] },
    { category: '추천 방향', examples: ['서쪽', '북서쪽'] },
    { category: '행운 아이템', examples: ['금속 소품', '시계', '보석'] },
    { category: '추천 직업', examples: ['법조', '금융', '기술', '제조'] },
  ],
  수: [
    { category: '행운 색상', examples: ['검정', '남색', '파랑'] },
    { category: '추천 방향', examples: ['북쪽'] },
    { category: '행운 아이템', examples: ['수족관', '물소리', '여행'] },
    { category: '추천 직업', examples: ['무역', 'IT', '관광', '연구'] },
  ],
};

export function extractYongshin(result: SajuResult, score: SingangyakScore): YongshinResult {
  const dayEl = ELEMENT_IDX[STEM_ELEMENTS[result.dayPillar.stemIdx]];
  const isWeak = score.total < 50;

  let targetEl: number;
  let reason: string;

  if (isWeak) {
    // 신약: 나를 생해주는 오행(인성) 또는 같은 오행(비겁)이 용신
    // 계절에서 가장 약한 쪽으로 결정
    const inseongEl = GENERATED_BY[dayEl]; // 인성
    const bijepEl = dayEl; // 비겁
    // 계절 힘이 낮은 쪽을 보강
    const monthBranchIdx = result.monthPillar.branchIdx;
    const seasonalPowerBijep = SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === bijepEl) as Element][monthBranchIdx];
    const seasonalPowerInseong = SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === inseongEl) as Element][monthBranchIdx];
    
    // 인성이 계절력이 있으면 인성, 없으면 비겁
    if (seasonalPowerInseong >= 3) {
      targetEl = inseongEl;
      reason = '나를 키워주는 인성(印星) 기운이 가장 필요해요';
    } else {
      targetEl = bijepEl;
      reason = '나와 같은 비겁(比劫) 기운으로 힘을 키워야 해요';
    }
  } else {
    // 신강: 나의 기운을 소진시키는 식상(食傷), 재성(財星), 관성(官星) 중 택일
    const siksangEl = GENERATES[dayEl]; // 식상 (내가 생하는)
    const jaeseongEl = CONTROLS[dayEl]; // 재성 (내가 극하는)
    const gwanseongEl = CONTROLLED_BY[dayEl]; // 관성 (나를 극하는)

    const monthBranchIdx = result.monthPillar.branchIdx;
    // 가장 약한 오행을 용신으로 (보강 효과 극대화)
    const powers = [
      { el: siksangEl, label: '식상(食傷)', power: SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === siksangEl) as Element][monthBranchIdx] },
      { el: jaeseongEl, label: '재성(財星)', power: SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === jaeseongEl) as Element][monthBranchIdx] },
      { el: gwanseongEl, label: '관성(官星)', power: SEASON_POWER[Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === gwanseongEl) as Element][monthBranchIdx] },
    ].sort((a, b) => a.power - b.power);

    targetEl = powers[0].el;
    reason = `강한 기운을 조절하는 ${powers[0].label} 기운이 균형을 잡아줘요`;
  }

  const element = Object.keys(ELEMENT_IDX).find(k => ELEMENT_IDX[k as Element] === targetEl) as Element;
  const isWeakStr = isWeak ? '신약' : '신강';
  const tossMessage = isWeak
    ? `${score.emoji} 지금 가장 필요한 행운의 열쇠는 '${ELEMENT_NAMES[element]}'이에요! ${ELEMENT_KEYWORDS[element]}의 기운으로 나를 채워보세요.`
    : `${score.emoji} 강한 에너지를 다스려줄 '${ELEMENT_NAMES[element]}'이 균형을 맞춰줄 거예요! ${ELEMENT_KEYWORDS[element]}을 가까이 하세요.`;

  return {
    element,
    reason,
    description: `${isWeakStr}인 사주에서 ${ELEMENT_NAMES[element]} 기운이 부족한 부분을 채워줘요.`,
    tossMessage,
    items: ELEMENT_ITEMS[element],
  };
}

// 가중치 오행 분포 계산 (월지 계절 가중치 포함)
export function getWeightedElementDist(result: SajuResult): Record<Element, number> {
  const dist: Record<Element, number> = { 목: 0, 화: 0, 토: 0, 금: 0, 수: 0 };
  const monthBranchIdx = result.monthPillar.branchIdx;

  const pillars = [result.yearPillar, result.monthPillar, result.dayPillar, ...(result.hourPillar ? [result.hourPillar] : [])];

  for (const pillar of pillars) {
    const isMonth = pillar.label === '월주';
    const stemWeight = isMonth ? 1.5 : 1.0;
    const branchWeight = isMonth ? 2.0 : 1.2;

    const stemEl = STEM_ELEMENTS[pillar.stemIdx];
    const branchEl = BRANCH_ELEMENTS[pillar.branchIdx];

    dist[stemEl] += stemWeight;
    dist[branchEl] += branchWeight;

    // 지장간 포함 (정기 0.5, 중기 0.25, 초기 0.1)
    const jijangEntries = JIJANG[pillar.branchIdx];
    const roleWeights = { '정기': 0.5, '중기': 0.25, '초기': 0.1 };
    for (const entry of jijangEntries) {
      const jEl = STEM_ELEMENTS[entry.stemIdx];
      dist[jEl] += roleWeights[entry.role] * (isMonth ? 1.5 : 1.0);
    }
  }

  // 계절 가중치 적용
  for (const el of Object.keys(dist) as Element[]) {
    const seasonPower = SEASON_POWER[el][monthBranchIdx];
    dist[el] *= (0.6 + seasonPower * 0.15); // 1.5 ~ 1.8 범위 배율
  }

  return dist;
}
